import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { FILE_UPLOAD_ERROR_MESSAGE } from 'src/constants/messages';
import { IconSelectorModal } from './IconSelectorModal';
import { SafeIcon } from 'src/components/SafeIcon/SafeIcon';

interface IconFieldProps {
  value: string;
  onChange: (value: string) => void;
  isSelected: boolean;
  onClick: () => void;
}

export const IconField: React.FC<IconFieldProps> = ({ value, onChange, onClick }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const { uploadPermanentIcon } = useAssetsApi();

  const uploadFile = async (file: File) => {
    if (!selectedCompanyId) return;
    setUploading(true);
    try {
      const permanentUrl = await uploadPermanentIcon(file, selectedCompanyId);
      onChange(permanentUrl);
    } catch (error) {
      reportErrorToUser(error, FILE_UPLOAD_ERROR_MESSAGE);
    } finally {
      setUploading(false);
      // Clear the input so the same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = (file: File | null) => {
    if (file) {
      void uploadFile(file);
    }
  };

  const handleButtonClick = () => {
    setModalOpen(true);
  };

  const handleSelectIcon = (iconName: string) => {
    onChange(iconName);
  };

  const handleUploadIcon = (file: File) => {
    handleFileChange(file);
  };

  const renderIcon = () => {
    if (value.startsWith('data:image/') || value.startsWith('http')) {
      return (
        <Box
          component="img"
          src={value}
          alt="Campaign Icon"
          width={40}
          height={40}
          borderRadius={1}
          sx={{ objectFit: 'cover' }}
        />
      );
    } else {
      return (
        <Box display="flex" color="text.secondary" fontSize={40}>
          <SafeIcon name={value} fontSize="large" fallback={<ImageIcon fontSize="inherit" />} />
        </Box>
      );
    }
  };

  return (
    <>
      <Box onClick={onClick}>
        <Box display="flex" alignItems="center" gap={2}>
          {renderIcon()}

          <Button
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadFileIcon />}
            disabled={uploading}
            sx={{
              height: '40px',
              width: value ? '170px' : '220px',
            }}
            onClick={e => {
              e.stopPropagation();
              handleButtonClick();
            }}
          >
            {uploading ? 'Uploading...' : value ? 'Change Icon' : 'Add Icon'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={event => handleFileChange(event.target.files?.[0] ?? null)}
          />
        </Box>
      </Box>

      <IconSelectorModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelectIcon={handleSelectIcon}
        onUploadIcon={handleUploadIcon}
      />
    </>
  );
};
